import React, { useState } from 'react';
import { 
  Building2, 
  MapPin, 
  FileText, 
  CreditCard, 
  CheckCircle, 
  Upload, 
  Clock,
  Shield,
  ArrowLeft,
  ArrowRight,
  Star,
  AlertCircle,
  X
} from 'lucide-react';
import api from '../utils/axios';

const VendorOnboarding = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState('');
  const [formData, setFormData] = useState({
    business: { name: '', subCategories: '' },
    address: { 
      city: '', 
      district: '', 
      postalCode: '', 
      region: '', 
      street: '', 
      latitude: '', 
      longitude: '',
      country: 'India'
    },
    documents: { 
      businessLicense: '', 
      taxRegistration: '', 
      identityProof: '', 
      bankProof: '', 
      fssaiCertificate: '' 
    },
    payout: { bankAccount: '', upiId: '' }
  });

  const steps = [
    { id: 1, title: 'Business Info', icon: Building2, required: true },
    { id: 2, title: 'Address Details', icon: MapPin, required: true },
    { id: 3, title: 'Documents', icon: FileText, required: false },
    { id: 4, title: 'Payout Setup', icon: CreditCard, required: false },
    { id: 5, title: 'Review & Submit', icon: CheckCircle, required: true }
  ];

  // Validation rules for each step [web:21][web:31]
  const validateStep = (step) => {
    const newErrors = {};
    setApiError('');

    switch (step) {
      case 1:
        if (!formData.business.name.trim()) {
          newErrors.businessName = 'Business name is required';
        }
        if (!formData.business.subCategories) {
          newErrors.businessCategory = 'Business category is required';
        }
        break;
      
      case 2:
        if (!formData.address.street.trim()) {
          newErrors.street = 'Street address is required';
        }
        if (!formData.address.city.trim()) {
          newErrors.city = 'City is required';
        }
        if (!formData.address.district.trim()) {
          newErrors.district = 'District is required';
        }
        if (!formData.address.region.trim()) {
          newErrors.region = 'State/Region is required';
        }
        if (!formData.address.postalCode.trim()) {
          newErrors.postalCode = 'Postal code is required';
        } else if (!/^\d{6}$/.test(formData.address.postalCode)) {
          newErrors.postalCode = 'Please enter a valid 6-digit postal code';
        }
        break;
      
      default:
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // API function to update vendor onboarding data [web:18][web:36]
  const updateVendorData = async () => {
    try {
      // Transform form data to match your schema
      const apiPayload = {
        business_name: formData.business.name,
        address: {
          street: formData.address.street,
          city: formData.address.city,
          district: formData.address.district,
          state: formData.address.region,
          zip: formData.address.postalCode,
          country: formData.address.country,
          ...(formData.address.latitude && formData.address.longitude && {
            coordinates: {
              latitude: parseFloat(formData.address.latitude),
              longitude: parseFloat(formData.address.longitude)
            }
          })
        },
        services: [{
          serviceType: getServiceTypeFromCategory(formData.business.subCategories),
          subCategories: [formData.business.subCategories],
          isLive: true
        }],
        documents: {
          businessLicense: formData.documents.businessLicense || null,
          taxRegistration: formData.documents.taxRegistration || null,
          identityProof: formData.documents.identityProof || null,
          bankProof: formData.documents.bankProof || null,
          fssaiCertificate: formData.documents.fssaiCertificate || null
        },
        payoutInfo: {
          bankAccount: formData.payout.bankAccount || null,
          upiId: formData.payout.upiId || null
        },
        // Update onboarding step tracking
        onboardingSteps: {
          business: !!formData.business.name,
          address: !!(formData.address.street && formData.address.city),
          services: !!formData.business.subCategories,
          documents: Object.values(formData.documents).some(doc => doc),
          payout: !!(formData.payout.bankAccount || formData.payout.upiId)
        }
      };

      const response = await api.put('/vendors/onboarding', apiPayload);
      return response.data;
    } catch (error) {
      console.error('API Error:', error);
      throw error;
    }
  };

  // API function to submit final application [web:25][web:29]
  const submitApplication = async () => {
    try {
      const response = await api.post('/vendors/onboarding/submit');
      return response.data;
    } catch (error) {
      console.error('Submit Error:', error);
      throw error;
    }
  };

  // Helper function to map UI categories to service types
  const getServiceTypeFromCategory = (category) => {
    const categoryMap = {
      'convention_hall': 'venue',
      'restaurant': 'catering',
      'catering': 'catering',
      'photography': 'photography',
      'decoration': 'event_service',
      'music': 'event_service'
    };
    return categoryMap[category] || 'other';
  };

  const handleNext = async () => {
    if (currentStep < 5) {
      // Validate current step before moving forward
      if (steps[currentStep - 1].required && !validateStep(currentStep)) {
        return;
      }

      setCurrentStep(currentStep + 1);
      
      // Save data to API when moving between steps [web:23]
      try {
        await updateVendorData();
      } catch (error) {
        setApiError('Failed to save data. Please try again.');
        console.error('Save error:', error);
      }
    } else {
      // Final submission
      if (!validateStep(currentStep)) return;
      
      setLoading(true);
      try {
        // First update the vendor data
        await updateVendorData();
        // Then submit for review
        await submitApplication();
        
        setIsSubmitted(true);
      } catch (error) {
        setApiError(
          error.response?.data?.message || 
          'Failed to submit application. Please try again.'
        );
      } finally {
        setLoading(false);
      }
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = async () => {
    if (currentStep < 5) {
      setCurrentStep(currentStep + 1);
      
      // Save current data even when skipping
      try {
        await updateVendorData();
      } catch (error) {
        console.error('Save error on skip:', error);
      }
    }
  };

  // Error Alert Component
  const ErrorAlert = ({ message, onClose }) => (
    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start justify-between">
      <div className="flex items-start space-x-2">
        <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
        <p className="text-red-700 text-sm">{message}</p>
      </div>
      <button
        onClick={onClose}
        className="text-red-400 hover:text-red-600"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-purple-50 font-['Inter'] flex items-center justify-center p-4">
        <div className="max-w-2xl w-full bg-white rounded-2xl sm:rounded-3xl shadow-xl border border-gray-100 p-4 sm:p-6 lg:p-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 bg-gradient-to-br from-purple-400 to-pink-600 rounded-full mb-4 sm:mb-6 mx-auto">
            <CheckCircle className="w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 text-white" />
          </div>
          <h1 className="font-['Manrope'] text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">Application Submitted!</h1>
          <p className="font-['Inter'] text-base sm:text-lg lg:text-xl text-gray-600 mb-6 sm:mb-8 px-2">
            Thank you for joining our platform. Your vendor application has been successfully submitted.
          </p>
          
          <div className="bg-gray-50 rounded-xl lg:rounded-2xl p-4 sm:p-6 mb-6 sm:mb-8 border border-gray-100">
            <div className="flex flex-col sm:flex-row items-center justify-center mb-3 sm:mb-4">
              <Clock className="w-6 h-6 sm:w-8 sm:h-8 text-amber-500 mb-2 sm:mb-0 sm:mr-3" />
              <h3 className="font-['Manrope'] text-lg sm:text-xl lg:text-2xl font-semibold text-gray-900 text-center sm:text-left">Review Process</h3>
            </div>
            <p className="font-['Inter'] text-gray-700 text-sm sm:text-base lg:text-lg leading-relaxed px-2">
              Our team will carefully review your application within <span className="text-amber-600 font-semibold">24-48 hours</span>. 
              We'll verify your business information, documents, and ensure everything meets our quality standards.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8">
            <div className="bg-purple-50 rounded-lg sm:rounded-xl p-3 sm:p-4 border border-purple-100">
              <Shield className="w-6 h-6 sm:w-8 sm:h-8 text-purple-600 mb-2 mx-auto sm:mx-0" />
              <h4 className="font-['Manrope'] font-semibold text-gray-900 mb-1 text-center sm:text-left text-sm sm:text-base">Secure Review</h4>
              <p className="font-['Inter'] text-xs sm:text-sm text-gray-600 text-center sm:text-left">Your data is protected during our verification process</p>
            </div>
            <div className="bg-yellow-50 rounded-lg sm:rounded-xl p-3 sm:p-4 border border-yellow-100">
              <Star className="w-6 h-6 sm:w-8 sm:h-8 text-yellow-600 mb-2 mx-auto sm:mx-0" />
              <h4 className="font-['Manrope'] font-semibold text-gray-900 mb-1 text-center sm:text-left text-sm sm:text-base">Quality Check</h4>
              <p className="font-['Inter'] text-xs sm:text-sm text-gray-600 text-center sm:text-left">We ensure only the best vendors join our platform</p>
            </div>
          </div>

          <div className="font-['Inter'] text-gray-600 text-sm sm:text-base px-2">
            <p className="mb-2">You'll receive an email notification once the review is complete.</p>
            <p>Questions? Contact our support team anytime.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-white font-['Inter']">
      {/* Brand panel with step tracker */}
      <aside className="sticky top-0 hidden h-screen w-[36%] max-w-md flex-col justify-between overflow-hidden bg-gradient-to-br from-purple-600 to-pink-600 p-12 text-white lg:flex">
        <div className="pointer-events-none absolute -right-24 -top-24 h-80 w-80 rounded-full bg-white/10 blur-2xl" />
        <div className="pointer-events-none absolute -bottom-32 -left-20 h-96 w-96 rounded-full bg-white/10 blur-2xl" />

        <p className="relative font-['Manrope'] text-2xl font-extrabold tracking-tight">MandapAI</p>

        <div className="relative">
          <h1 className="font-['Manrope'] text-3xl font-bold leading-tight tracking-tight">
            Set up your vendor profile
          </h1>
          <p className="mt-3 text-sm text-white/80">
            Complete your onboarding in a few simple steps and start growing your business with us.
          </p>

          <ol className="mt-10">
            {steps.map((step, index) => {
              const Icon = step.icon;
              const isActive = currentStep === step.id;
              const isCompleted = currentStep > step.id;

              return (
                <li key={step.id} className="relative flex gap-4 pb-7 last:pb-0">
                  {index < steps.length - 1 && (
                    <span
                      className={`absolute left-5 top-10 h-[calc(100%-2.5rem)] w-px ${
                        isCompleted ? 'bg-white' : 'bg-white/25'
                      }`}
                    />
                  )}
                  <span
                    className={`relative flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full transition-all duration-300 ${
                      isCompleted
                        ? 'bg-white text-purple-600'
                        : isActive
                        ? 'bg-white text-purple-600 ring-4 ring-white/30'
                        : 'bg-white/15 text-white/70'
                    }`}
                  >
                    {isCompleted ? <CheckCircle className="h-5 w-5" /> : <Icon className="h-5 w-5" />}
                  </span>
                  <div className="pt-1.5">
                    <p className={`text-sm font-semibold ${isActive || isCompleted ? 'text-white' : 'text-white/70'}`}>
                      {step.title}
                    </p>
                    {!step.required && <p className="text-xs text-white/60">Optional</p>}
                  </div>
                </li>
              );
            })}
          </ol>
        </div>

        <p className="relative text-xs text-white/70">
          © {new Date().getFullYear()} MandapAI · India's event marketplace
        </p>
      </aside>

      <div className="min-w-0 flex-1">
      <div className="mx-auto max-w-3xl px-5 py-8 sm:px-8 lg:py-14">
        {/* Compact progress (shown when the brand panel is hidden) */}
        <div className="mb-8 lg:hidden">
          <p className="font-['Manrope'] text-xl font-extrabold tracking-tight text-gray-900">
            Mandap<span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">AI</span>
          </p>
          <div className="mt-5 flex items-center justify-between">
            <div className="text-sm text-gray-500">Step {currentStep} of {steps.length}</div>
            <div className="text-sm font-medium text-gray-900">{steps[currentStep - 1].title}</div>
          </div>
          <div className="mt-2 h-1.5 rounded-full bg-gray-100">
            <div
              className="h-1.5 rounded-full bg-gradient-to-r from-purple-600 to-pink-600 transition-all duration-300"
              style={{ width: `${(currentStep / steps.length) * 100}%` }}
            ></div>
          </div>
        </div>

        {/* Form Content */}
        <div>
          {/* Error Alert */}
          {apiError && (
            <ErrorAlert 
              message={apiError} 
              onClose={() => setApiError('')} 
            />
          )}

          {currentStep === 1 && (
            <div className="space-y-6">
              <div className="text-center mb-6 sm:mb-8">
                <div className="inline-flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 bg-gradient-to-br from-purple-100 to-pink-100 rounded-full mb-3 sm:mb-4">
                  <Building2 className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8 text-purple-600" />
                </div>
                <h2 className="font-['Manrope'] text-2xl sm:text-3xl font-bold text-gray-900 mb-2 px-4">Business Information</h2>
                <p className="font-['Inter'] text-gray-600 text-sm sm:text-base px-4">Tell us about your business to get started</p>
              </div>
              
              <div className="space-y-4 sm:space-y-6 max-w-2xl mx-auto">
                <div>
                  <label className="block font-['Inter'] text-sm font-medium text-gray-700 mb-2">Business Name *</label>
                  <input
                    type="text"
                    value={formData.business.name}
                    onChange={(e) => handleInputChange('business', 'name', e.target.value)}
                    className={`w-full px-3 sm:px-4 py-3 border rounded-lg font-['Inter'] focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors duration-200 text-sm sm:text-base ${
                      errors.businessName ? 'border-red-300 bg-red-50' : 'border-gray-300'
                    }`}
                    placeholder="Enter your business name"
                  />
                  {errors.businessName && (
                    <p className="mt-1 text-sm text-red-600">{errors.businessName}</p>
                  )}
                </div>
                
                <div>
                  <label className="block font-['Inter'] text-sm font-medium text-gray-700 mb-2">Business Category *</label>
                  <select
                    value={formData.business.subCategories}
                    onChange={(e) => handleInputChange('business', 'subCategories', e.target.value)}
                    className={`w-full px-3 sm:px-4 py-3 border rounded-lg font-['Inter'] focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors duration-200 text-sm sm:text-base ${
                      errors.businessCategory ? 'border-red-300 bg-red-50' : 'border-gray-300'
                    }`}
                  >
                    <option value="">Select your business category</option>
                    <option value="convention_hall">Convention Hall</option>
                    <option value="banquet_hall">Banquet Hall</option>
                    <option value="lawn_garden">Lawn & Garden</option>
                    <option value="wedding_catering">Wedding Catering</option>
                    <option value="corporate_catering">Corporate Catering</option>
                    <option value="wedding_photography">Wedding Photography</option>
                    <option value="event_coverage">Event Coverage</option>
                    <option value="decoration">Decoration Services</option>
                    <option value="music_dj">Music & DJ Services</option>
                  </select>
                  {errors.businessCategory && (
                    <p className="mt-1 text-sm text-red-600">{errors.businessCategory}</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {currentStep === 2 && (
            <div className="space-y-6">
              <div className="text-center mb-6 sm:mb-8">
                <div className="inline-flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 bg-gradient-to-br from-purple-100 to-pink-100 rounded-full mb-3 sm:mb-4">
                  <MapPin className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8 text-purple-600" />
                </div>
                <h2 className="font-['Manrope'] text-2xl sm:text-3xl font-bold text-gray-900 mb-2 px-4">Address Details</h2>
                <p className="font-['Inter'] text-gray-600 text-sm sm:text-base px-4">Where is your business located?</p>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 max-w-4xl mx-auto">
                <div className="sm:col-span-2">
                  <label className="block font-['Inter'] text-sm font-medium text-gray-700 mb-2">Street Address *</label>
                  <input
                    type="text"
                    value={formData.address.street}
                    onChange={(e) => handleInputChange('address', 'street', e.target.value)}
                    className={`w-full px-3 sm:px-4 py-3 border rounded-lg font-['Inter'] focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors duration-200 text-sm sm:text-base ${
                      errors.street ? 'border-red-300 bg-red-50' : 'border-gray-300'
                    }`}
                    placeholder="Enter street address"
                  />
                  {errors.street && (
                    <p className="mt-1 text-sm text-red-600">{errors.street}</p>
                  )}
                </div>
                
                <div>
                  <label className="block font-['Inter'] text-sm font-medium text-gray-700 mb-2">City *</label>
                  <input
                    type="text"
                    value={formData.address.city}
                    onChange={(e) => handleInputChange('address', 'city', e.target.value)}
                    className={`w-full px-3 sm:px-4 py-3 border rounded-lg font-['Inter'] focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors duration-200 text-sm sm:text-base ${
                      errors.city ? 'border-red-300 bg-red-50' : 'border-gray-300'
                    }`}
                    placeholder="Enter city name"
                  />
                  {errors.city && (
                    <p className="mt-1 text-sm text-red-600">{errors.city}</p>
                  )}
                </div>
                
                <div>
                  <label className="block font-['Inter'] text-sm font-medium text-gray-700 mb-2">District *</label>
                  <input
                    type="text"
                    value={formData.address.district}
                    onChange={(e) => handleInputChange('address', 'district', e.target.value)}
                    className={`w-full px-3 sm:px-4 py-3 border rounded-lg font-['Inter'] focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors duration-200 text-sm sm:text-base ${
                      errors.district ? 'border-red-300 bg-red-50' : 'border-gray-300'
                    }`}
                    placeholder="Enter district"
                  />
                  {errors.district && (
                    <p className="mt-1 text-sm text-red-600">{errors.district}</p>
                  )}
                </div>
                
                <div>
                  <label className="block font-['Inter'] text-sm font-medium text-gray-700 mb-2">State/Region *</label>
                  <input
                    type="text"
                    value={formData.address.region}
                    onChange={(e) => handleInputChange('address', 'region', e.target.value)}
                    className={`w-full px-3 sm:px-4 py-3 border rounded-lg font-['Inter'] focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors duration-200 text-sm sm:text-base ${
                      errors.region ? 'border-red-300 bg-red-50' : 'border-gray-300'
                    }`}
                    placeholder="Enter state or region"
                  />
                  {errors.region && (
                    <p className="mt-1 text-sm text-red-600">{errors.region}</p>
                  )}
                </div>
                
                <div>
                  <label className="block font-['Inter'] text-sm font-medium text-gray-700 mb-2">Postal Code *</label>
                  <input
                    type="text"
                    value={formData.address.postalCode}
                    onChange={(e) => handleInputChange('address', 'postalCode', e.target.value)}
                    className={`w-full px-3 sm:px-4 py-3 border rounded-lg font-['Inter'] focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors duration-200 text-sm sm:text-base ${
                      errors.postalCode ? 'border-red-300 bg-red-50' : 'border-gray-300'
                    }`}
                    placeholder="Enter postal code"
                  />
                  {errors.postalCode && (
                    <p className="mt-1 text-sm text-red-600">{errors.postalCode}</p>
                  )}
                </div>
                
                <div className="sm:col-span-2">
                  <label className="block font-['Inter'] text-sm font-medium text-gray-700 mb-2">Coordinates (Optional)</label>
                  <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2">
                    <input
                      type="number"
                      step="any"
                      value={formData.address.latitude}
                      onChange={(e) => handleInputChange('address', 'latitude', e.target.value)}
                      className="flex-1 px-3 sm:px-4 py-3 border border-gray-300 rounded-lg font-['Inter'] focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors duration-200 text-sm sm:text-base"
                      placeholder="Latitude"
                    />
                    <input
                      type="number"
                      step="any"
                      value={formData.address.longitude}
                      onChange={(e) => handleInputChange('address', 'longitude', e.target.value)}
                      className="flex-1 px-3 sm:px-4 py-3 border border-gray-300 rounded-lg font-['Inter'] focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors duration-200 text-sm sm:text-base"
                      placeholder="Longitude"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Steps 3, 4, and 5 remain the same as in your original code */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <div className="text-center mb-6 sm:mb-8">
                <div className="inline-flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 bg-gradient-to-br from-purple-100 to-pink-100 rounded-full mb-3 sm:mb-4">
                  <FileText className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8 text-purple-600" />
                </div>
                <h2 className="font-['Manrope'] text-2xl sm:text-3xl font-bold text-gray-900 mb-2 px-4">Document Upload</h2>
                <p className="font-['Inter'] text-gray-600 text-sm sm:text-base px-4 text-center">Upload your business documents (Optional but recommended for faster approval)</p>
              </div>
              
              <div className="space-y-3 sm:space-y-4 max-w-3xl mx-auto">
                {[
                  { key: 'businessLicense', label: 'Business License', placeholder: 'Business registration certificate' },
                  { key: 'taxRegistration', label: 'Tax Registration (GSTIN)', placeholder: 'GST registration document' },
                  { key: 'identityProof', label: 'Identity Proof', placeholder: 'Aadhaar, PAN, or Passport' },
                  { key: 'bankProof', label: 'Bank Proof', placeholder: 'Bank statement or passbook' },
                  { key: 'fssaiCertificate', label: 'FSSAI Certificate', placeholder: 'Food business license (if applicable)' }
                ].map((doc) => (
                  <div key={doc.key} className="p-4 sm:p-6 bg-gray-50 rounded-lg sm:rounded-xl border border-gray-200">
                    <label className="block font-['Inter'] text-sm font-medium text-gray-700 mb-2 sm:mb-3">{doc.label}</label>
                    <div className="flex items-center space-x-2 sm:space-x-3">
                      <div className="flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 bg-white rounded-lg border border-gray-200 flex-shrink-0">
                        <Upload className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500" />
                      </div>
                      <input
                        type="url"
                        value={formData.documents[doc.key]}
                        onChange={(e) => handleInputChange('documents', doc.key, e.target.value)}
                        className="flex-1 px-3 sm:px-4 py-3 bg-white border border-gray-300 rounded-lg font-['Inter'] focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors duration-200 text-sm sm:text-base"
                        placeholder={`Enter ${doc.label.toLowerCase()} URL or upload link`}
                      />
                    </div>
                    <p className="font-['Inter'] text-xs text-gray-500 mt-2">{doc.placeholder}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {currentStep === 4 && (
            <div className="space-y-6">
              <div className="text-center mb-6 sm:mb-8">
                <div className="inline-flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 bg-gradient-to-br from-purple-100 to-pink-100 rounded-full mb-3 sm:mb-4">
                  <CreditCard className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8 text-purple-600" />
                </div>
                <h2 className="font-['Manrope'] text-2xl sm:text-3xl font-bold text-gray-900 mb-2 px-4">Payout Setup</h2>
                <p className="font-['Inter'] text-gray-600 text-sm sm:text-base px-4 text-center">Configure how you'd like to receive payments (Optional - can be set up later)</p>
              </div>
              
              <div className="space-y-4 sm:space-y-6 max-w-2xl mx-auto">
                <div>
                  <label className="block font-['Inter'] text-sm font-medium text-gray-700 mb-2">Bank Account Number</label>
                  <input
                    type="text"
                    value={formData.payout.bankAccount}
                    onChange={(e) => handleInputChange('payout', 'bankAccount', e.target.value)}
                    className="w-full px-3 sm:px-4 py-3 border border-gray-300 rounded-lg font-['Inter'] focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors duration-200 text-sm sm:text-base"
                    placeholder="Enter your bank account number"
                  />
                </div>
                
                <div>
                  <label className="block font-['Inter'] text-sm font-medium text-gray-700 mb-2">UPI ID</label>
                  <input
                    type="text"
                    value={formData.payout.upiId}
                    onChange={(e) => handleInputChange('payout', 'upiId', e.target.value)}
                    className="w-full px-3 sm:px-4 py-3 border border-gray-300 rounded-lg font-['Inter'] focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors duration-200 text-sm sm:text-base"
                    placeholder="Enter UPI ID (e.g., yourname@paytm)"
                  />
                </div>
                
                <div className="bg-amber-50 border border-amber-200 rounded-lg sm:rounded-xl p-3 sm:p-4">
                  <div className="flex items-start space-x-2 sm:space-x-3">
                    <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                    <p className="font-['Inter'] text-amber-700 text-xs sm:text-sm">
                      You can set up payout details later in your dashboard if you prefer to skip this step. This won't affect your application approval.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {currentStep === 5 && (
            <div className="space-y-6">
              <div className="text-center mb-6 sm:mb-8">
                <div className="inline-flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 bg-gradient-to-br from-purple-100 to-pink-100 rounded-full mb-3 sm:mb-4">
                  <CheckCircle className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8 text-purple-600" />
                </div>
                <h2 className="font-['Manrope'] text-2xl sm:text-3xl font-bold text-gray-900 mb-2 px-4">Review & Submit</h2>
                <p className="font-['Inter'] text-gray-600 text-sm sm:text-base px-4 text-center">Please review your information before submitting your application</p>
              </div>
              
              <div className="space-y-4 sm:space-y-6">
                <div className="bg-gray-50 rounded-lg sm:rounded-xl p-4 sm:p-6 border border-gray-200">
                  <h3 className="font-['Manrope'] text-lg sm:text-xl font-semibold text-gray-900 mb-3 sm:mb-4 flex items-center">
                    <Building2 className="w-4 h-4 sm:w-5 sm:h-5 mr-2 text-purple-600 flex-shrink-0" />
                    <span className="text-sm sm:text-lg">Business Information</span>
                  </h3>
                  <div className="space-y-2">
                    <p className="font-['Inter'] text-gray-700 text-sm sm:text-base break-words">
                      <span className="font-medium text-gray-900">Name:</span> {formData.business.name || 'Not provided'}
                    </p>
                    <p className="font-['Inter'] text-gray-700 text-sm sm:text-base break-words">
                      <span className="font-medium text-gray-900">Category:</span> {formData.business.subCategories || 'Not selected'}
                    </p>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg sm:rounded-xl p-4 sm:p-6 border border-gray-200">
                  <h3 className="font-['Manrope'] text-lg sm:text-xl font-semibold text-gray-900 mb-3 sm:mb-4 flex items-center">
                    <MapPin className="w-4 h-4 sm:w-5 sm:h-5 mr-2 text-purple-600 flex-shrink-0" />
                    <span className="text-sm sm:text-lg">Address Details</span>
                  </h3>
                  <div className="space-y-2">
                    <p className="font-['Inter'] text-gray-700 text-sm sm:text-base break-words">
                      {[formData.address.street, formData.address.district, formData.address.city, formData.address.region, formData.address.postalCode]
                        .filter(Boolean).join(', ') || 'Not provided'}
                    </p>
                    {(formData.address.latitude && formData.address.longitude) && (
                      <p className="font-['Inter'] text-gray-600 text-xs sm:text-sm break-all">
                        Coordinates: {formData.address.latitude}, {formData.address.longitude}
                      </p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                  <div className="bg-gray-50 rounded-lg sm:rounded-xl p-4 sm:p-6 border border-gray-200">
                    <h3 className="font-['Manrope'] text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 flex items-center">
                      <FileText className="w-4 h-4 sm:w-5 sm:h-5 mr-2 text-purple-600 flex-shrink-0" />
                      <span>Documents</span>
                    </h3>
                    <p className="font-['Inter'] text-gray-700 text-sm">
                      {Object.values(formData.documents).filter(Boolean).length} documents uploaded
                    </p>
                  </div>

                  <div className="bg-gray-50 rounded-lg sm:rounded-xl p-4 sm:p-6 border border-gray-200">
                    <h3 className="font-['Manrope'] text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 flex items-center">
                      <CreditCard className="w-4 h-4 sm:w-5 sm:h-5 mr-2 text-purple-600 flex-shrink-0" />
                      <span>Payout Setup</span>
                    </h3>
                    <p className="font-['Inter'] text-gray-700 text-sm">
                      {(formData.payout.bankAccount || formData.payout.upiId) ? 'Configured' : 'Not configured (can be set up later)'}
                    </p>
                  </div>
                </div>

                <div className="bg-purple-50 border border-purple-200 rounded-lg sm:rounded-xl p-4 sm:p-6">
                  <h3 className="font-['Manrope'] text-base sm:text-lg font-semibold text-gray-900 mb-2 sm:mb-3 flex items-center">
                    <Clock className="w-4 h-4 sm:w-5 sm:h-5 mr-2 text-purple-600 flex-shrink-0" />
                    <span>What happens next?</span>
                  </h3>
                  <ul className="font-['Inter'] text-purple-700 text-xs sm:text-sm space-y-1 sm:space-y-2">
                    <li className="flex items-start space-x-2">
                      <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 text-purple-600 mt-0.5 flex-shrink-0" />
                      <span>Your application will be reviewed within 24-48 hours</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 text-purple-600 mt-0.5 flex-shrink-0" />
                      <span>We'll verify your business information and documents</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 text-purple-600 mt-0.5 flex-shrink-0" />
                      <span>You'll receive an email notification once approved</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 text-purple-600 mt-0.5 flex-shrink-0" />
                      <span>After approval, you can start listing your services immediately</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center mt-8 sm:mt-12 pt-6 border-t border-gray-200 space-y-3 sm:space-y-0">
            <button
              onClick={handlePrevious}
              disabled={currentStep === 1}
              className={`flex items-center justify-center space-x-2 px-4 sm:px-6 py-3 mt-3 sm:mt-0 rounded-lg font-['Inter'] font-medium transition-all order-2 sm:order-1 ${
                currentStep === 1
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300 shadow-sm'
              }`}
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Previous</span>
            </button>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-3 sm:space-y-0 sm:space-x-4 order-1 sm:order-2">
              {!steps[currentStep - 1]?.required && currentStep < 5 && (
                <button
                  onClick={handleSkip}
                  className="px-4 sm:px-6 py-3 font-['Inter'] text-gray-600 hover:text-gray-800 transition-colors text-center sm:text-left text-sm sm:text-base"
                >
                  Skip this step
                </button>
              )}
              
              <button
                onClick={handleNext}
                disabled={loading}
                className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 disabled:from-purple-400 disabled:to-pink-400 disabled:cursor-not-allowed text-white font-['Manrope'] font-semibold py-3 sm:py-3 px-2 rounded-lg transition-all duration-300 transform hover:scale-105 disabled:hover:scale-100 flex items-center justify-center space-x-2 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 min-w-full sm:min-w-[200px] text-sm sm:text-base"
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-white border-t-transparent rounded-full animate-spin mt-0.5"></div>
                    <span>Submitting...</span>
                  </>
                ) : (
                  <>
                    <span>{currentStep === 5 ? 'Submit Application' : 'Continue'}</span>
                    <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default VendorOnboarding;
